import { z } from 'zod';
import {
  conversationModeSchema,
  withdrawalRiskSchema,
  validatedOnboardingAnswersSchema,
} from '../onboarding-sessions/schema';
import { apiEnvelope } from '../envelope';
import { intakeFocusConfigSchema } from '../plans/intake-capabilities';

export const INTAKE_SETUP_OPERATIONS = [
  'initialize',
  'initialize_web',
  'save_app_setup',
  'save_conversation_mode',
  'record_stop',
  'acknowledge_end',
  'resolve_feedback',
  'confirm_plan',
  'confirm_reminder',
  'complete',
] as const;

export const INTAKE_SETUP_DISPOSITIONS = [
  'unresolved',
  'normal',
  'manual',
  'interrupted_unknown',
] as const;
export const INTAKE_SETUP_FEEDBACK = [
  'not_due',
  'pending',
  'submitted',
  'skipped',
  'suppressed',
] as const;
export const INTAKE_SETUP_STATUSES = ['in_progress', 'completed', 'superseded'] as const;
export const INTAKE_SETUP_REMINDER = [
  'unresolved',
  'existing',
  'saved',
  'off_without_row',
] as const;

export const appOnboardingStepSchema = z.enum([
  'registered_summary',
  'name',
  'sona_intro',
  'conversation_mode',
  'done',
]);
export const appOnboardingValuesSchema = z.object({
  '/screen/preferred_name': z.string().trim().min(1).optional(),
  '/screen/conversation_mode': z.enum(['talk', 'text', 'none']).optional(),
});
export const appOnboardingSchema = z.object({
  version: z.literal(1),
  step: appOnboardingStepSchema,
  nameStepRequired: z.boolean().optional(),
  values: appOnboardingValuesSchema,
});
export type AppOnboarding = z.infer<typeof appOnboardingSchema>;
export const appSetupSummarySchema = z.object({
  preferredName: z.string().optional(),
  programKeys: z.array(z.string()),
  goalValues: z.array(z.string()),
  motivationValues: z.array(z.string()),
  motivationOther: z.string().optional(),
  reportedWeek: z
    .object({
      anchorDate: z.string(),
      total: z.number().nonnegative(),
      freeDays: z.number().int().min(0).max(7),
      programKey: z.string(),
      captureMode: z.enum(['counter', 'yes-no']).optional(),
      /** Server-selected catalog messages; installed clients do not map programs to units. */
      totalLabelKey: z.string().min(1),
      freeDaysLabelKey: z.string().min(1),
    })
    .optional(),
  appointment: z.object({ id: z.string().uuid(), startsAt: z.string().datetime() }).optional(),
});
export type AppSetupSummary = z.infer<typeof appSetupSummarySchema>;

export const intakeSetupGoalSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('tracking_only') }).strict(),
  z
    .object({
      mode: z.literal('weekly_quantity_limit'),
      value: z.number().int().nonnegative(),
      memberProgramId: z.string().uuid(),
    })
    .strict(),
]);
export const intakeSetupFocusSchema = intakeFocusConfigSchema;
export const intakeSetupPlanInputSchema = z
  .object({
    goal: intakeSetupGoalSchema,
    focus: intakeSetupFocusSchema.optional(),
  })
  .strict();

/** Durable facts only: output existence and next-step eligibility are read from their owners. */
export const intakeSetupFactsSchema = z.object({
  disposition: z.enum(INTAKE_SETUP_DISPOSITIONS),
  sessionId: z.string().uuid().nullable(),
  endAcknowledged: z.boolean(),
  feedback: z.enum(INTAKE_SETUP_FEEDBACK),
  reminderConfirmation: z.enum(INTAKE_SETUP_REMINDER),
  reminderId: z.string().uuid().nullable(),
  confirmedPlanId: z.string().uuid().nullable(),
  pendingFocus: intakeSetupFocusSchema.strip().nullable(),
});
export const storedIntakeSetupFactsSchema = intakeSetupFactsSchema.extend({
  appOnboarding: appOnboardingSchema.optional(),
});
export type IntakeSetupFacts = z.infer<typeof storedIntakeSetupFactsSchema>;
export const initialIntakeSetupFacts: IntakeSetupFacts = {
  disposition: 'unresolved',
  sessionId: null,
  endAcknowledged: false,
  feedback: 'pending',
  reminderConfirmation: 'unresolved',
  reminderId: null,
  confirmedPlanId: null,
  pendingFocus: null,
};

const mutationFields = {
  requestKey: z.string().uuid(),
  attemptId: z.string().uuid(),
  expectedRevision: z.number().int().nonnegative(),
};
export const intakeSetupCommandSchema = z.discriminatedUnion('operation', [
  z
    .object({
      operation: z.literal('initialize'),
      requestKey: z.string().uuid(),
      flowId: z.string().min(1).max(100),
    })
    .strict(),
  z
    .object({
      ...mutationFields,
      operation: z.literal('record_stop'),
      sessionId: z.string().uuid().nullable(),
    })
    .strict(),
  z
    .object({
      ...mutationFields,
      operation: z.literal('acknowledge_end'),
      sessionId: z.string().uuid(),
      disposition: z.enum(['normal', 'manual']),
      focus: intakeSetupFocusSchema.nullable(),
    })
    .strict(),
  z
    .object({
      ...mutationFields,
      operation: z.literal('resolve_feedback'),
      outcome: z.enum(['submitted', 'skipped', 'suppressed']),
    })
    .strict(),
  z
    .object({
      ...mutationFields,
      operation: z.literal('confirm_plan'),
      sessionId: z.string().uuid().optional(),
      plan: intakeSetupPlanInputSchema,
    })
    .strict(),
  z
    .object({
      ...mutationFields,
      operation: z.literal('confirm_reminder'),
      outcome: z.enum(['existing', 'saved', 'off_without_row']),
      reminderId: z.string().uuid().nullable(),
    })
    .strict(),
  z.object({ ...mutationFields, operation: z.literal('complete') }).strict(),
]);
export const appSetupCommandSchema = z.discriminatedUnion('operation', [
  ...intakeSetupCommandSchema.options,
  z
    .object({
      operation: z.literal('initialize_web'),
      requestKey: z.string().uuid(),
      flowId: z.string().min(1).max(100),
    })
    .strict(),
  z
    .object({
      ...mutationFields,
      operation: z.literal('save_conversation_mode'),
      mode: conversationModeSchema,
    })
    .strict(),
  z
    .object({
      ...mutationFields,
      operation: z.literal('save_app_setup'),
      action: z.enum(['save_values', 'continue', 'start', 'continue_without_sona']),
      step: appOnboardingStepSchema,
      values: appOnboardingValuesSchema.strict(),
    })
    .strict(),
]);
export type IntakeSetupCommand = z.infer<typeof intakeSetupCommandSchema>;
export type AppSetupCommand = z.infer<typeof appSetupCommandSchema>;
export const webQuestionnaireCompletionRequestSchema = z
  .object({
    questionnaireSessionId: z.string().uuid(),
    answers: validatedOnboardingAnswersSchema,
  })
  .strict();
export type IntakeSetupPlanInput = z.infer<typeof intakeSetupPlanInputSchema>;

export const intakeSetupAttemptSchema = z.object({
  id: z.string().uuid(),
  revision: z.number().int().nonnegative(),
  flowId: z.string(),
  status: z.enum(INTAKE_SETUP_STATUSES),
  facts: intakeSetupFactsSchema,
  completedAt: z.string().datetime().nullable(),
});
export const storedIntakeSetupAttemptSchema = intakeSetupAttemptSchema.extend({
  facts: storedIntakeSetupFactsSchema,
});
export type IntakeSetupAttempt = z.infer<typeof storedIntakeSetupAttemptSchema>;
export const intakeSetupEvidenceSchema = z.object({
  withdrawalRisk: withdrawalRiskSchema.nullable().optional(),
  plan: z.enum(['saved', 'pending', 'missing', 'unavailable']),
  reminder: z.enum(['present', 'absent', 'unavailable']),
  withdrawal: z.enum(['owed', 'resolved', 'unavailable']),
});
export type IntakeSetupEvidence = z.infer<typeof intakeSetupEvidenceSchema>;
export const intakeSetupPlanSummarySchema = z.object({
  id: z.string().uuid(),
  focus: intakeSetupFocusSchema.strip().nullable(),
  goal: z.discriminatedUnion('mode', [
    intakeSetupGoalSchema.options[0].strip(),
    intakeSetupGoalSchema.options[1].strip(),
  ]),
});
export type IntakeSetupPlanSummary = z.infer<typeof intakeSetupPlanSummarySchema>;
export const intakeSetupReadSchema = z.object({
  planSummary: intakeSetupPlanSummarySchema.nullable().optional(),
  mode: z.enum(['legacy', 'in_progress', 'completed']),
  attempt: intakeSetupAttemptSchema.nullable(),
  evidence: intakeSetupEvidenceSchema,
  nextStep: z.enum([
    'legacy',
    'questions',
    'conversation',
    'feedback',
    'withdrawal',
    'plan',
    'reminder',
    'confirm',
    'complete',
    'hold',
  ]),
});
export const appSetupReadSchema = intakeSetupReadSchema.extend({
  attempt: storedIntakeSetupAttemptSchema.nullable(),
  appOnboardingEligible: z.boolean().optional(),
  questionnaireSessionId: z.string().uuid().optional(),
  summary: appSetupSummarySchema.nullable().optional(),
});
export type IntakeSetupRead = z.infer<typeof appSetupReadSchema>;
export const appSetupResponseSchema = apiEnvelope(appSetupReadSchema);
export const intakeSetupResponseSchema = apiEnvelope(intakeSetupReadSchema);

export function resolveIntakeSetupNextStep(
  attempt: IntakeSetupAttempt | null,
  evidence: IntakeSetupEvidence,
): IntakeSetupRead['nextStep'] {
  if (!attempt) return 'legacy';
  if (attempt.status === 'completed') return 'complete';
  if (attempt.status !== 'in_progress') return 'hold';
  const { facts } = attempt;
  if (facts.disposition === 'unresolved' || facts.disposition === 'interrupted_unknown')
    return 'conversation';
  if (!facts.endAcknowledged || facts.feedback === 'pending') return 'feedback';
  // A client-reported normal end proves neither screening nor clinical
  // completion. Only canonical screening evidence can clear this obligation.
  if (evidence.withdrawal === 'unavailable') return 'hold';
  if (evidence.withdrawal === 'owed') return 'withdrawal';
  if (
    evidence.plan === 'pending' ||
    evidence.plan === 'unavailable' ||
    evidence.reminder === 'unavailable'
  )
    return 'hold';
  if (evidence.plan === 'missing' || !facts.confirmedPlanId) return 'plan';
  if (facts.reminderConfirmation === 'unresolved') return 'reminder';
  return 'confirm';
}
